// Reports screen — Phase 4 Plan 04-06 Task 2 (DIS-19, DIS-21, D-76).
// Migrated to telegram-ui — Phase 12 Plan 12-09 Task 2 (UI-SPEC §7 Wave 2).
//
// Owner + Dispatcher gated screen with two panels:
//   1. "Generate report" form: report_type radio (5 named types), from/to
//      date pickers, format SegmentedControl (xlsx | pdf), [Generate].
//   2. "Recent reports" list: last 10 reports from GET /reports?limit=10,
//      polled every 3s WHILE any row is in 'pending' / 'generating'.
//      Per row: [Download] when ready, [Retry] on failed, [Regenerate] on
//      expired.
//
// Generate flow (logic PRESERVED — UI-SPEC §10 out-of-scope):
//   - Server returns 200 + .xlsx blob (sync fast-path) → triggerBlobDownload.
//   - Server returns 202 + ReportResponse (async) → invalidate list query so
//     the new row appears immediately; polling picks up the eventual ready.
//   - Server returns 400 "Period too large" → banner + form preserved.
//
// telegram-ui migration (UI-SPEC §6/§7):
//   - screen shell → <List>; field groups → <Section>+<Cell>; recent list → <Cell>
//   - errors → <Banner type="section"> (role=alert preserved, data-testid kept)
//   - SegmentedControl (format) preserved; secondary buttons → <Button>
//   - local decodeJwtPayload removed → canonical lib/jwt (DRY consolidation)
//   - hardcode-hex / inline font-family removed; theme vars (--tgui--*) only

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Banner, Button, Cell, List, Section, Text, Title } from "@telegram-apps/telegram-ui";

import {
  type ReportFormat,
  type ReportLocale,
  type ReportResponse,
  type ReportType,
  reportsApi,
  triggerBlobDownload,
} from "../api/reports";
import { useAuth } from "../auth/useAuth";
import { SegmentedControl } from "../components/SegmentedControl";
import { decodeJwtPayload } from "../lib/jwt";

// --------------------------------------------------------------------------- //
// Role gate (UX-only — server enforces require_role(["owner","dispatcher"]))
// --------------------------------------------------------------------------- //

function useUserRole(): { role: string | null } {
  const { jwt } = useAuth();
  return useMemo(() => {
    const payload = decodeJwtPayload(jwt);
    return { role: payload?.role ?? null };
  }, [jwt]);
}

const ALLOWED_ROLES = new Set(["owner", "dispatcher"]);

// --------------------------------------------------------------------------- //
// Helpers
// --------------------------------------------------------------------------- //

const REPORT_TYPES: ReportType[] = [
  "trips_period",
  "per_driver_summary",
  "per_customer_summary",
  "problems_log",
  "monthly_summary",
];

function defaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  // YYYY-MM-DD for <input type="date">
  const fmt = (d: Date): string => d.toISOString().slice(0, 10);
  return { from: fmt(thirtyDaysAgo), to: fmt(now) };
}

function toIsoStart(dateStr: string): string {
  if (!dateStr) return "";
  return `${dateStr}T00:00:00Z`;
}

function toIsoEnd(dateStr: string): string {
  if (!dateStr) return "";
  return `${dateStr}T23:59:59Z`;
}

function pickLocale(): ReportLocale {
  // Read from i18next language; default to 'en'. Only en/tr/bg supported by
  // the report template registry.
  const lng = (typeof navigator !== "undefined" ? navigator.language : "en")
    .slice(0, 2)
    .toLowerCase();
  if (lng === "tr" || lng === "bg") return lng;
  return "en";
}

// --------------------------------------------------------------------------- //
// Reports screen
// --------------------------------------------------------------------------- //

export interface ReportsProps {
  baseUrl: string;
}

export function Reports({ baseUrl }: ReportsProps): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient, jwt } = useAuth();
  const queryClient = useQueryClient();
  const { role } = useUserRole();

  const allowed = role !== null && ALLOWED_ROLES.has(role);

  const initial = defaultDateRange();
  const [reportType, setReportType] = useState<ReportType>("trips_period");
  const [format, setFormat] = useState<ReportFormat>("xlsx");
  const [fromDate, setFromDate] = useState<string>(initial.from);
  const [toDate, setToDate] = useState<string>(initial.to);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["reports", "list"],
    queryFn: () => reportsApi.list(apiClient, { limit: 10 }),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const hasInflight = data.items.some(
        (r) => r.status === "pending" || r.status === "generating",
      );
      return hasInflight ? 3000 : false;
    },
    enabled: allowed,
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      setErrorBanner(null);
      const result = await reportsApi.generate(baseUrl, jwt, {
        report_type: reportType,
        format,
        filters: {
          from: fromDate ? toIsoStart(fromDate) : null,
          to: toDate ? toIsoEnd(toDate) : null,
        },
        locale: pickLocale(),
      });
      return result;
    },
    onSuccess: async (result) => {
      if (result.kind === "sync") {
        triggerBlobDownload(result.blob, result.filename);
      }
      // Refresh the list either way — async path needs to show the new row,
      // sync path may also have produced an audit-only row (none in MVP, but
      // future-proof).
      await queryClient.invalidateQueries({ queryKey: ["reports", "list"] });
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : t("dispatcher:reports.error_generic", "Generate failed");
      setErrorBanner(msg);
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (reportId: number) => reportsApi.regenerate(apiClient, reportId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reports", "list"] });
    },
  });

  if (!allowed) {
    return (
      <List data-testid="reports-forbidden" style={{ padding: 16 }}>
        <Text>
          {t("dispatcher:reports.owner_dispatcher_only", "Owner or dispatcher access required")}
        </Text>
      </List>
    );
  }

  const generateDisabled = !fromDate || !toDate || !reportType || generateMutation.isPending;

  return (
    <List data-testid="reports-screen" style={{ padding: 16 }}>
      <Title level="2" weight="2">
        {t("dispatcher:reports.title", "Reports")}
      </Title>

      {/* Generate form */}
      <Section
        data-testid="reports-generate-form"
        header={t("dispatcher:reports.generate_heading", "Generate report")}
      >
        <fieldset style={{ border: "none", padding: "8px 16px", marginBottom: 0 }}>
          <legend>{t("dispatcher:reports.type_label", "Report type")}</legend>
          {REPORT_TYPES.map((rt) => (
            <label key={rt} style={{ display: "block", padding: 4 }}>
              <input
                type="radio"
                name="report_type"
                value={rt}
                checked={reportType === rt}
                onChange={() => setReportType(rt)}
                data-testid={`report-type-${rt}`}
              />
              <span style={{ marginLeft: 8 }}>{t(`dispatcher:reports.type.${rt}`, rt)}</span>
            </label>
          ))}
        </fieldset>

        <div style={{ display: "flex", gap: 12, padding: "0 16px 12px" }}>
          <label style={{ flex: 1 }}>
            {t("dispatcher:reports.from_label", "From")}
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="report-from"
              style={{ display: "block", width: "100%" }}
            />
          </label>
          <label style={{ flex: 1 }}>
            {t("dispatcher:reports.to_label", "To")}
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="report-to"
              style={{ display: "block", width: "100%" }}
            />
          </label>
        </div>

        <div style={{ padding: "0 16px 12px" }}>
          <label style={{ display: "block", marginBottom: 4 }} id="format-label">
            {t("dispatcher:reports.format_label", "Format")}
          </label>
          <SegmentedControl
            value={format}
            onChange={(v) => setFormat(v as ReportFormat)}
            options={[
              {
                value: "xlsx",
                label: t("dispatcher:reports.format_xlsx", "Excel"),
              },
              {
                value: "pdf",
                label: t("dispatcher:reports.format_pdf", "PDF"),
              },
            ]}
            ariaLabel={t("dispatcher:reports.format_label", "Format")}
          />
        </div>

        {errorBanner && (
          <Banner
            type="section"
            role="alert"
            data-testid="report-error-banner"
            header={t("dispatcher:reports.error_heading", "Report error")}
            description={errorBanner}
            style={{ marginBottom: 12 }}
          />
        )}

        <div style={{ padding: "0 16px 12px" }}>
          <Button
            mode="filled"
            size="l"
            stretched
            disabled={generateDisabled}
            loading={generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
            data-testid="report-generate-btn"
          >
            {generateMutation.isPending
              ? t("dispatcher:reports.generating", "Generating…")
              : t("dispatcher:reports.generate_btn", "Generate report")}
          </Button>
        </div>
      </Section>

      {/* Recent list */}
      <Section
        data-testid="reports-list-section"
        header={t("dispatcher:reports.recent_heading", "Recent reports")}
      >
        {listQuery.isLoading && (
          <Cell>
            <Text>{t("common:loading", "Loading…")}</Text>
          </Cell>
        )}
        {listQuery.data && listQuery.data.items.length === 0 && (
          <Cell data-testid="reports-list-empty">
            {t("dispatcher:reports.list_empty", "No reports yet.")}
          </Cell>
        )}
        {listQuery.data &&
          listQuery.data.items.length > 0 &&
          listQuery.data.items.map((r) => (
            <ReportRow key={r.id} report={r} onRegenerate={() => regenerateMutation.mutate(r.id)} />
          ))}
      </Section>
    </List>
  );
}

// --------------------------------------------------------------------------- //
// ReportRow — single list item
// --------------------------------------------------------------------------- //

interface ReportRowProps {
  report: ReportResponse;
  onRegenerate: () => void;
}

function ReportRow({ report, onRegenerate }: ReportRowProps): React.JSX.Element {
  const { t } = useTranslation();

  const after =
    report.status === "ready" && report.download_url ? (
      <Button
        mode="filled"
        size="s"
        Component="a"
        href={report.download_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`report-download-${report.id}`}
      >
        {t("dispatcher:reports.download_btn", "Download")}
      </Button>
    ) : report.status === "failed" || report.status === "expired" ? (
      <Button
        mode="bezeled"
        size="s"
        onClick={onRegenerate}
        data-testid={`report-regenerate-${report.id}`}
      >
        {t("dispatcher:reports.regenerate_btn", "Regenerate")}
      </Button>
    ) : undefined;

  return (
    <Cell
      data-testid={`report-row-${report.id}`}
      data-status={report.status}
      multiline
      after={after}
      subtitle={
        <>
          {new Date(report.created_at).toLocaleString()} ·{" "}
          {t(`dispatcher:reports.status.${report.status}`, report.status)}
          {report.error_message && (
            <span
              data-testid={`report-error-${report.id}`}
              style={{ display: "block", color: "var(--tgui--destructive_text_color)" }}
            >
              {report.error_message}
            </span>
          )}
        </>
      }
    >
      {t(`dispatcher:reports.type.${report.report_type}`, report.report_type)} (
      {report.format.toUpperCase()})
    </Cell>
  );
}
